import type { ReportChartType, ReportDefinition } from '@ofmos/shared/report';

/**
 * THE TAKEAWAY LINE (`CRM-CORE-SCREENS-COMPOSITION-2026-09-06.md` section 4).
 *
 * One sentence above the chart saying what the chart says, with the number in it:
 * "Revenue from closed deals is up 18% vs last month". It exists because a chart answers a
 * question only for somebody already looking for the answer - the line is what makes a
 * report readable in a glance from a doorway, and it is what a person quotes in a meeting.
 *
 * A TEMPLATE PER CHART TYPE, NEVER A MODEL. The owner's instruction is explicit and it is
 * also the only defensible engineering: a sentence generated from the numbers is
 * REPRODUCIBLE, it costs nothing, it cannot hallucinate a trend that is not in the data,
 * and it says the same thing every time the same numbers do. An LLM here would be a
 * plausible sentence about a chart nobody checked.
 *
 * WHAT EACH TEMPLATE CLAIMS, and each is the largest true statement its shape supports:
 *  - a TIME SERIES (bar, line, area over a date bucket) claims the MOVEMENT between the
 *    last two buckets, which is the question a monthly chart is read for;
 *  - a CATEGORY chart (bar over a select) claims the LARGEST category and its share;
 *  - a DONUT claims the largest slice's share of the whole, which is what a ring is for;
 *  - a FUNNEL claims the end-to-end conversion and the worst single step, because the
 *    step that leaks is the only actionable thing a funnel says;
 *  - a BIG NUMBER states the number and the window, because there is nothing to compare
 *    it to and inventing a comparison would be inventing data.
 *
 * IT REFUSES RATHER THAN GUESSES. Fewer than two comparable buckets is no movement, an
 * all-null series is no share, and both return null - the page then shows no line at all
 * rather than "up 0%", which is a claim about data that does not exist.
 */

export interface TakeawayPoint {
  readonly label: string;
  readonly value: number | null;
}

export interface TakeawayInput {
  readonly chart: ReportChartType;
  /** In the chart's own order: calendar order for a series, funnel order for a funnel. */
  readonly points: readonly TakeawayPoint[];
  /** How the metric is written. The page passes its own formatter so units match. */
  readonly format: (value: number) => string;
  /** "Last 12 Months", "This Quarter" - the window's own words, for the big number. */
  readonly rangeLabel: string;
  /** True when the group-by is a date bucket, which is what makes a series a series. */
  readonly overTime: boolean;
}

/** Whole percentages: a takeaway read to one decimal place is precision nobody acts on. */
function percent(value: number): string {
  return `${String(Math.round(Math.abs(value)))}%`;
}

function change(from: number, to: number): string | null {
  // A move from nothing has no percentage - "up infinity" is not a sentence - so the
  // template falls back to the absolute pair, which is still true.
  if (from === 0) return null;
  return percent(((to - from) / Math.abs(from)) * 100);
}

const known = (points: readonly TakeawayPoint[]) =>
  points.filter((point): point is TakeawayPoint & { value: number } => point.value !== null);

export function takeawayLine(input: TakeawayInput): string | null {
  const { chart, points, format } = input;
  if (chart === 'number') return bigNumber(input);
  const real = known(points);
  if (real.length === 0) return null;

  if (chart === 'funnel') return funnel(real, format);
  if (chart === 'donut') return share(real, format);
  if (input.overTime && real.length >= 2) return movement(real, format);
  return share(real, format);
}

/** The movement between the last two buckets, which is what a monthly chart is read for. */
function movement(
  points: readonly (TakeawayPoint & { value: number })[],
  format: (value: number) => string,
): string {
  const last = points[points.length - 1];
  const previous = points[points.length - 2];
  if (last === undefined || previous === undefined) {
    // Unreachable through `takeawayLine`, which only calls this with two or more; a
    // fallback that returned null would make the caller's type wider for a case that
    // cannot happen.
    return share(points, format) ?? '';
  }

  const direction =
    last.value === previous.value
      ? 'level with'
      : last.value > previous.value
        ? 'up from'
        : 'down from';
  const delta = change(previous.value, last.value);
  const size = delta === null ? '' : ` ${delta}`;
  if (last.value === previous.value) {
    return `${last.label} is level with ${previous.label} at ${format(last.value)}.`;
  }
  return `${last.label} is${size} ${direction} ${previous.label}: ${format(last.value)} against ${format(previous.value)}.`;
}

/** The largest category and its share of the whole, which is what a ring or a bar says. */
function share(
  points: readonly (TakeawayPoint & { value: number })[],
  format: (value: number) => string,
): string | null {
  const positive = points.filter((point) => point.value > 0);
  if (positive.length === 0) return null;
  const total = positive.reduce((sum, point) => sum + point.value, 0);
  const largest = positive.reduce((best, point) => (point.value > best.value ? point : best));
  if (positive.length === 1) {
    return `${largest.label} is the only one with anything in it: ${format(largest.value)}.`;
  }
  return `${largest.label} is the largest at ${format(largest.value)}, ${percent((largest.value / total) * 100)} of the total.`;
}

/**
 * The end-to-end conversion AND the worst step. Two numbers rather than one, because the
 * first says how the funnel is doing and only the second says where to push.
 */
function funnel(
  points: readonly (TakeawayPoint & { value: number })[],
  format: (value: number) => string,
): string | null {
  const first = points[0];
  const last = points[points.length - 1];
  if (first === undefined || last === undefined || first.value <= 0) return null;
  const overall = percent((last.value / first.value) * 100);
  if (points.length < 3) {
    return `${overall} of ${format(first.value)} reach ${last.label}.`;
  }

  let worst = { rate: Number.POSITIVE_INFINITY, from: first, to: first };
  for (let index = 1; index < points.length; index += 1) {
    const from = points[index - 1];
    const to = points[index];
    if (from === undefined || to === undefined || from.value <= 0) continue;
    const rate = to.value / from.value;
    if (rate < worst.rate) worst = { rate, from, to };
  }
  if (!Number.isFinite(worst.rate))
    return `${overall} of ${format(first.value)} reach ${last.label}.`;
  return `${overall} of ${format(first.value)} reach ${last.label}; the biggest drop is ${worst.from.label} to ${worst.to.label}, where ${percent((1 - worst.rate) * 100)} fall away.`;
}

/** The number and the window. Nothing to compare it to, so nothing is compared. */
function bigNumber(input: TakeawayInput): string | null {
  const only = known(input.points)[0];
  if (only === undefined) return null;
  return `${input.format(only.value)} over ${input.rangeLabel.toLowerCase()}.`;
}

/** Whether the chart's group-by makes it a time series, which decides the template. */
export function isOverTime(definition: ReportDefinition): boolean {
  return definition.groupBy?.granularity !== undefined;
}
